import { Theme, useMediaQuery } from '@mui/material';
import DateRangePicker, { DatePickerRangeValue } from '@forms/Components/DateRangePicker';
import { Toolbar } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { CheckInput } from '@forms/Components/CheckInput';
import { EdiStatus } from '@api/Trinium/Endpoints/EDI/types';
import { StyledQuickFilter } from '../StyledComponents';
import RowActionsMenuCell from '../ColumnDefinitions/RowActionsMenuCell';
import MobileFilterMenu from '../MobileFilterMenu';
import RowActionList from './ColumnDefinitions/RowActionList';
import ButtonDropdown from '@forms/Components/Dropdowns/ButtonDropdown';
import PopupList from '@forms/Components/Dropdowns/List';
import { useEdiLoadTenderContext } from './EdiLoadTenderContext';

import styles from './EdiDataGrid.module.css';
import useDebouncer from '@hooks/useDebounce';

export default function EdiGridToolbar() {
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	if (isMobile) {
		return (
			<Toolbar className={styles['edi-grid-toolbar']}>
				<RowActionsMenuCell rowIdProperty='id'>
					<RowActionList />
				</RowActionsMenuCell>

				<MobileFilterMenu>
					<EdiDataGridFilterItems />
				</MobileFilterMenu>
			</Toolbar>
		);
	}

	return (
		<Toolbar className={styles['edi-grid-toolbar']}>
			<EdiDataGridFilterItems />

			<StyledQuickFilter
				className={styles['quick-filter']}
				inputClassName={styles['quick-filter-input']}
			/>
		</Toolbar>
	);
}

/**
 * Abstracting the actual Filter Components just to not recode for Mobile and Desktop renders
 */
function EdiDataGridFilterItems() {
	const ediContext = useEdiLoadTenderContext();
	const [localRangePickerState, setLocalRangePickerState] = useState<DatePickerRangeValue>([
		ediContext.modifyDateFrom,
		ediContext.modifyDateTo
	]);

	const isCompact = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

	const rangePickerOnChange = (value: DatePickerRangeValue) => {
		setLocalRangePickerState(value);
	};

	const onEdiStatusChange = (value: keyof typeof EdiStatus) => {
		ediContext.setEdiStatus(value);
	};

	useDebouncer(
		() => {
			ediContext.setDateRange(localRangePickerState);
		},
		[localRangePickerState],
		600
	);

	return (
		<>
			{isCompact ? (
				<ButtonDropdown
					label='EDI Status:'
					ButtonContent={(props: { status: string }) => <>{props.status}</>}
					buttonContentProps={{ status: ediContext.ediStatus }}
					buttonConfig={{ className: styles.test }}
					PopupContent={PopupList}
					popupContentProps={{
						items: ['All', 'Imported', 'Accepted', 'Auto-Accepted', 'Rejected', 'Deleted', 'Error'],
						getItemDisplay: (item: string) => item,
						onItemSelect: (item: string) => {
							onEdiStatusChange(item as keyof typeof EdiStatus);
						}
					}}
				/>
			) : (
				<fieldset className={styles['fieldset']}>
					<legend className={styles['legend']}>EDI Status:</legend>
					<CheckInput
						label='All'
						labelPlacement='start'
						value={ediContext.ediStatus === 'All'}
						onChange={() => {
							onEdiStatusChange('All');
						}}
					/>
					<CheckInput
						label='Imported'
						labelPlacement='start'
						value={ediContext.ediStatus === 'Imported'}
						onChange={() => {
							onEdiStatusChange('Imported');
						}}
					/>
					<CheckInput
						label='Accepted'
						labelPlacement='start'
						value={ediContext.ediStatus === 'Accepted'}
						onChange={() => {
							onEdiStatusChange('Accepted');
						}}
					/>
					<CheckInput
						label='Auto-Accepted'
						labelPlacement='start'
						value={ediContext.ediStatus === 'Auto-Accepted'}
						onChange={() => {
							onEdiStatusChange('Auto-Accepted');
						}}
					/>
					<CheckInput
						label='Rejected'
						labelPlacement='start'
						value={ediContext.ediStatus === 'Rejected'}
						onChange={() => {
							onEdiStatusChange('Rejected');
						}}
					/>
					<CheckInput
						label='Deleted'
						labelPlacement='start'
						value={ediContext.ediStatus === 'Deleted'}
						onChange={() => {
							onEdiStatusChange('Deleted');
						}}
					/>
					<CheckInput
						label='Error'
						labelPlacement='start'
						value={ediContext.ediStatus === 'Error'}
						onChange={() => {
							onEdiStatusChange('Error');
						}}
					/>
				</fieldset>
			)}
			<DateRangePicker
				value={localRangePickerState}
				onChange={rangePickerOnChange}
				customProps={{
					labelText: 'Modified Date:',
					isDirty: false,
					isError: false,
					variant: 'outlined',
					onClear: () => {
						ediContext.setDateRange([null, null]);
					}
				}}
				className={styles['created-date-filter-input']}
			/>
		</>
	);
}
